import type { ReactElement } from 'react';
import { NavLink } from 'react-router';

import { useAuth } from '../auth/AuthProvider.js';
import { useBranding } from '../branding/BrandingProvider.js';

import { NAV, type NavItem } from './nav.js';

/**
 * Renders only what the user both has permission for and whose feature is on.
 *
 * **Absent from the DOM, not disabled** (criterion 11). A disabled item still
 * advertises a capability — it tells a viewer that "Deactivate SIM" exists and
 * that someone can press it — and a customer who has not bought messaging
 * should not be shown a greyed-out messaging section as a permanent upsell in
 * their own operations tool. `queryBy…` returning null is the assertion.
 *
 * And, as everywhere in this folder: hiding is presentation. The API refuses
 * the same request whether or not the link was rendered.
 */
export function Sidebar({ onNavigate }: { onNavigate?: () => void }): ReactElement {
  const { hasPermission, hasFeature } = useAuth();
  const { branding } = useBranding();

  const visible = NAV.filter((item) => isVisible(item, hasPermission, hasFeature));
  const primary = visible.filter((item) => item.section === 'primary');
  const secondary = visible.filter((item) => item.section === 'secondary');

  const renderItem = (item: NavItem): ReactElement => (
    <NavLink
      key={item.to}
      to={item.to}
      onClick={onNavigate}
      className={({ isActive }) =>
        /**
         * §16.3.1: a left indicator and body text, never a brand tint behind
         * text. The only tenant-coloured pixels are the 2px border, which
         * carries no text — so the contrast holds whatever the tenant's hue is.
         *
         * The active row **inverts rather than tints**. The rail is now the
         * receding `surface-alt` plane, so the bright `surface` is available as
         * the "you are here" signal and costs no new measured colour pair: body
         * text on the same surface the rest of the app measures against. A
         * brand-tinted active row would need fresh §16.3 arithmetic and a new
         * source-scan guard, and buys nothing this does not already give.
         */
        /*
          36px, the control height the rest of §16.2 is built around, so a rail
          item is the same object as a button rather than two pixels short of
          one. At three primary items the extra air costs nothing and reads as
          deliberate rather than as a list squeezed to fit.

          The active row also takes `--elevation-1`. On the receding `chrome`
          plane the bright fill alone is the "you are here" signal, and it
          disappears entirely for a tenant whose `surface` and `surface-alt` are
          the same colour — the hairline-and-shadow pair is what survives that,
          the same argument AppShell makes for its borders.
        */
        `flex h-9 items-center gap-[var(--space-3)] rounded-[var(--radius-md)] border-l-2 px-[var(--space-3)] text-[length:var(--text-sm)] transition-[background-color,color,box-shadow] duration-[var(--motion-fast)] ${
          isActive
            ? 'border-primary bg-surface font-medium text-body shadow-[var(--elevation-1)]'
            : 'border-transparent text-muted hover:bg-surface hover:text-body'
        }`
      }
    >
      {/*
        `aria-hidden`: the label beside it is the accessible name, so the icon
        must not be announced twice. `shrink-0` keeps the glyph from squashing
        when a label wraps. It inherits `currentColor`, so it dims and brightens
        with its own label rather than being a second thing to keep in step.
      */}
      <item.icon aria-hidden="true" size={16} strokeWidth={2} className="shrink-0" />
      {item.label}
    </NavLink>
  );

  return (
    <div className="flex h-full flex-col">
      {/*
        The brand block sits at exactly `--topbar-h` with a hairline under it,
        so the rail and the context bar beside it share one baseline instead of
        meeting at two different heights. The logo is whatever the payload gives
        and the product name is the fallback — nothing here is a hardcoded path.
      */}
      <div className="flex h-[var(--topbar-h)] shrink-0 items-center gap-[var(--space-2)] border-b border-line px-[var(--space-4)]">
        {branding?.logoUrl ? (
          // The alt text is the product name, not "logo": a screen reader
          // announcing "logo" tells the user nothing about whose panel this is.
          <img
            src={branding.logoUrl}
            alt={branding.productName}
            className="h-6 w-auto max-w-full object-contain"
          />
        ) : (
          <span className="truncate text-[length:var(--text-sm)] font-semibold tracking-[var(--tracking-tight)] text-body">
            {branding?.productName ?? 'Connect Panel'}
          </span>
        )}
      </div>

      <nav
        aria-label="Main"
        className="flex flex-col gap-[var(--space-4)] px-[var(--space-3)] py-[var(--space-4)]"
      >
        <div className="flex flex-col gap-0.5">{primary.map(renderItem)}</div>

        {/*
          Kept and reachable, just not competing with the three flows the client
          asked for. Same routes, same URLs — nothing was deleted.

          The group is separated by a rule rather than by a larger gap: at this
          density an extra 8px of air reads as an accident, whereas a hairline
          is unambiguously a boundary someone drew.
        */}
        {secondary.length > 0 ? (
          <div className="flex flex-col gap-0.5 border-t border-line pt-[var(--space-3)]">
            <p className="px-[var(--space-3)] pb-[var(--space-1)] text-[length:var(--text-2xs)] font-medium uppercase tracking-[var(--tracking-caps)] text-muted">
              Manage
            </p>
            {secondary.map(renderItem)}
          </div>
        ) : null}
      </nav>
    </div>
  );
}

export function isVisible(
  item: NavItem,
  hasPermission: (...keys: never[]) => boolean,
  hasFeature: (...keys: never[]) => boolean,
): boolean {
  if (!hasPermission(item.permission as never)) return false;
  if (item.feature && !hasFeature(item.feature as never)) return false;
  return true;
}
